import React, { useState } from 'react';
import axios from 'axios';
import Honeypot from '../components/Honeypot';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, MapPin, Globe, Send, Linkedin, Facebook } from 'lucide-react';
import { XIcon } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Contact", url: "/contact" }
  ];
  
  const [hp, setHp] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    companyWebsite: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      toast.error('Please enter a message (at least 10 characters)');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, { ...formData, orb_hp: hp });
      
      if (response.data) {
        toast.success('Thank you! We\'ll get back to you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          company: '',
          companyWebsite: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      // Show more specific error messages
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.detail;
        if (validationErrors && Array.isArray(validationErrors)) {
          const errorMsg = validationErrors[0]?.msg || 'Please check your form inputs';
          toast.error(errorMsg);
        } else {
          toast.error('Please check your form inputs and try again');
        }
      } else {
        toast.error('Something went wrong. Please try again or email us directly at contact@orboroi.com');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'contact@orboroi.com',
      link: 'mailto:contact@orboroi.com'
    },
    {
      icon: MapPin,
      title: 'Headquarters',
      content: 'Stockholm',
      link: null
    },
    {
      icon: Globe,
      title: 'Founded',
      content: '2022',
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="Contact Us - Talk to a Data Expert"
        description="Get in touch with Orboroi's AI data operations team. Schedule a consultation to discuss your annotation, validation, and model evaluation needs."
        canonical="/contact"
        type="website"
        breadcrumbs={breadcrumbs}
      />
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Let's Achieve <span className="text-[#379F7F]">Data Certainty</span> Together
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Schedule a consultation to discuss your AI data needs and discover how Orboroi 
              can help you deploy production-ready models faster.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <Card 
                  key={index}
                  className="bg-gray-800 border-gray-700 hover:border-[#379F7F] transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#379F7F]/10 p-3 rounded-lg">
                        <info.icon className="w-6 h-6 text-[#379F7F]" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">{info.title}</h3>
                        {info.link ? (
                          <a 
                            href={info.link}
                            className="text-gray-300 hover:text-[#379F7F] transition-colors duration-300"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-gray-300">{info.content}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Additional Info */}
              <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-[#379F7F]">
                <CardHeader>
                  <CardTitle className="text-white">Connect With Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 mb-4">
                    <a 
                      href="https://www.linkedin.com/company/orboroi"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#379F7F]/10 p-3 rounded-lg hover:bg-[#379F7F]/20 transition-colors duration-300"
                    >
                      <Linkedin className="w-6 h-6 text-[#379F7F]" />
                    </a>
                    <a 
                      href="https://www.facebook.com/orboroi"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#379F7F]/10 p-3 rounded-lg hover:bg-[#379F7F]/20 transition-colors duration-300"
                    >
                      <Facebook className="w-6 h-6 text-[#379F7F]" />
                    </a>
                    <a 
                      href="https://x.com/orboroi"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#379F7F]/10 p-3 rounded-lg hover:bg-[#379F7F]/20 transition-colors duration-300"
                    >
                      <XIcon className="w-6 h-6 text-[#379F7F]" />
                    </a>
                  </div>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• 99% first-batch acceptance rate</li>
                    <li>• Founded in 2022</li>
                    <li>• End-to-end AI lifecycle support</li>
                    <li>• Domain-specific expertise</li>
                    <li>• Scalable from pilot to production</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">
                    Schedule a Data Readiness Consultation
                  </CardTitle>
                  <CardDescription className="text-gray-300">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <Honeypot value={hp} onChange={setHp} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-white">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="bg-gray-900 border-gray-700 text-white focus:border-[#379F7F]"
                          placeholder="John Doe"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-white">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="bg-gray-900 border-gray-700 text-white focus:border-[#379F7F]"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-white">Company</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="bg-gray-900 border-gray-700 text-white focus:border-[#379F7F]"
                          placeholder="Your Company"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="companyWebsite" className="text-white">Company Website</Label>
                        <Input
                          id="companyWebsite"
                          name="companyWebsite"
                          type="url"
                          value={formData.companyWebsite}
                          onChange={handleChange}
                          className="bg-gray-900 border-gray-700 text-white focus:border-[#379F7F]"
                          placeholder="https://yourcompany.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label htmlFor="message" className="text-white">Message *</Label>
                        <span className="text-sm text-gray-400">
                          {formData.message.length} / 10 min
                        </span>
                      </div>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        minLength={10}
                        rows={6}
                        className="bg-gray-900 border-gray-700 text-white focus:border-[#379F7F]"
                        placeholder="Tell us about your project, timeline, and data needs... (minimum 10 characters)"
                      />
                      {formData.message.length > 0 && formData.message.length < 10 && (
                        <p className="text-sm text-orange-400">
                          Please enter at least {10 - formData.message.length} more character{10 - formData.message.length !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold text-lg py-6"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
